import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import apiService from '../../services/ApiService';
import FieldsInitializer from '../ModelDataFields/Input/Initializer';

const mapOutputModelData = (modelData: any): any => ({
    id: modelData.id.toString(),
    name: modelData.name,
    type: modelData.type,
    kind: modelData.kind,
    position: modelData.position,
    childrens: (modelData.childrens || []).map((e: any) => e.toString()),
    possibleChildrens: modelData.possible_childrens || [],
    childrensDefinition: modelData.childrens_definition || [],
    parentId: modelData.parent_id ? modelData.parent_id.toString() : null,
    relatedModelId: modelData.related_model_id,
    fields: FieldsInitializer(modelData.fields || {}),
    has_related_output: modelData.has_related_output,
    relatedModelDataId: modelData.related_model_id,
    isInconsiderable: modelData.is_inconsiderable,
    isDeletable: modelData.is_deletable,
    expanded: false,
    editing: false,
    anchorEl: null,
    sensitivityModelData: null,
    deleteModalOpen: false,
    detailsModalOpen: false,
    documentation: modelData.documentation,
    compute_error: modelData.compute_error || null,
});

interface ConceptTreeNodeProps {
    item: any;
    tree: { items: Record<string, any>; rootId: string };
    selectedItemId: string | null;
    onSelectItem: (id: string) => void;
    level: number;
    searchString: string;
    isMatchSearch: (item: any, tree: any, searchString: string) => boolean;
    currentEntity?: any;
}

function ConceptTreeNodeInner({
    item,
    tree,
    selectedItemId,
    onSelectItem,
    level,
    searchString,
    isMatchSearch,
    currentEntity,
}: ConceptTreeNodeProps) {
    const [expanded, setExpanded] = useState(false);
    const [childrenLoading, setChildrenLoading] = useState(false);
    const hasChildren = item.childrens && item.childrens.length > 0;
    const isSelected = selectedItemId === item.id;

    // Auto-expand when searching
    useEffect(() => {
        if (searchString) {
            setExpanded(true);
        }
    }, [searchString]);

    // Collapse when entity changes (sensitivity switch) since children are reloaded
    useEffect(() => {
        setExpanded(false);
    }, [currentEntity?.id]);

    const hasError = Object.values(item.fields || {}).some((f: any) => f.compute_error);

    const handleExpandClick = async (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!expanded && hasChildren) {
            // Lazy-load children if not already in tree
            const firstChildId = item.childrens[0];
            if (!tree.items[firstChildId]) {
                setChildrenLoading(true);
                try {
                    const response = await apiService.getOutputModelDataChildren(parseInt(item.id));
                    if (response?.ok) {
                        const children = await response.json();
                        for (const child of children) {
                            tree.items[child.id.toString()] = mapOutputModelData(child);
                        }

                        // Apply sensitivity overlay on newly loaded output children
                        if (currentEntity?.type === 'sensitivity') {
                            try {
                                const sensResponse = await apiService.getSensitivity(currentEntity.id);
                                if (sensResponse?.ok) {
                                    const sensData = await sensResponse.json();
                                    sensData.output?.forEach((sensObj: any) => {
                                        const id = sensObj.id.toString();
                                        if (tree.items[id]) {
                                            const mergedFields = { ...tree.items[id].fields };
                                            Object.entries(sensObj.fields || {}).forEach(([key, field]: [string, any]) => {
                                                mergedFields[key] = {
                                                    ...mergedFields[key],
                                                    value: field.value,
                                                    compute_error: field.compute_error,
                                                };
                                            });
                                            tree.items[id] = {
                                                ...tree.items[id],
                                                fields: FieldsInitializer(mergedFields),
                                            };
                                        }
                                    });
                                }
                            } catch (sensErr) {
                                console.error('Failed to apply sensitivity on output children:', sensErr);
                            }
                        }
                    }
                } catch (err) {
                    console.error('Failed to load output children:', err);
                }
                setChildrenLoading(false);
            }
        }
        setExpanded(!expanded);
    };

    return (
        <div>
            <div
                className={cn(
                    'flex items-center py-1.5 px-2 cursor-pointer rounded-r-md transition-colors text-sm',
                    isSelected
                        ? 'bg-slate-100 border-l-2 border-primary font-medium'
                        : 'hover:bg-slate-50 border-l-2 border-transparent'
                )}
                style={{ paddingLeft: level * 16 + 8 }}
                onClick={() => onSelectItem(item.id)}
            >
                {/* Chevron */}
                {hasChildren ? (
                    <button
                        onClick={handleExpandClick}
                        className="p-0.5 hover:bg-slate-200 rounded mr-1 shrink-0"
                    >
                        {childrenLoading ? (
                            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                        ) : (
                            <span
                                className={cn(
                                    'material-symbols-outlined text-[16px] transition-transform text-slate-400',
                                    expanded && 'rotate-90'
                                )}
                            >
                                chevron_right
                            </span>
                        )}
                    </button>
                ) : (
                    <span className="w-[24px] shrink-0" />
                )}

                {/* Name */}
                <span className="truncate text-slate-700">{item.name}</span>

                {/* Error indicator */}
                {hasError && (
                    <span className="material-symbols-outlined text-[14px] text-red-500 ml-auto shrink-0">
                        error
                    </span>
                )}
            </div>

            {/* Children */}
            {expanded && hasChildren && (
                <div>
                    {item.childrens.map((childId: string) => {
                        const child = tree.items[childId];
                        if (!child) return null;
                        if (searchString && !isMatchSearch(child, tree, searchString)) return null;
                        return (
                            <ConceptTreeNodeInner
                                key={childId}
                                item={child}
                                tree={tree}
                                selectedItemId={selectedItemId}
                                onSelectItem={onSelectItem}
                                level={level + 1}
                                searchString={searchString}
                                isMatchSearch={isMatchSearch}
                                currentEntity={currentEntity}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const ConceptTreeNode = React.memo(ConceptTreeNodeInner);
export default ConceptTreeNode;
